import router from '@/router'
import { AuthService, MemberService } from '@/services'
import Cookies from 'quasar/src/plugins/Cookies.js';
import { LoginParam } from '@/services/models'
import { MemberLogin, LogoutOptions } from '@/views/models'
import { EnumForceLogoutType, EnumHttpStatusCode, EnumTutorialStatus } from '@/common/models/enum'
import { LoginRsp, Tokens } from '@/services/models/Auth'
import MemberProfile from '@/services/models/Member/MemberProfile'
import { getSubdomain } from '@/plugins/env'

const ACCESS_TOKEN_KEY = 'cybersaas_access_token'
const REFRESH_TOKEN_KEY = 'cybersaas_refresh_token'

const auth = new AuthService()
const member = new MemberService()

export default {
  state: {
    user: {} as MemberProfile,
    tokens: {} as Tokens,
    permissions: [] as any[],
    tutorialStatus: EnumTutorialStatus.done as EnumTutorialStatus,
    userEmail: '',
    userPassword: '',
  },
  getters: {
    user: (state: any) => state.user,
    permissions: (state: any) => state.permissions,
    tutorialStatus: (state: any) => state.tutorialStatus,
    tokens: (state: any) => state.tokens,
    userEmail: (state: any) => state.userEmail,
    userPassword: (state: any) => state.userPassword,
  },
  mutations: {
    setUser(state: any, member: MemberProfile) {
      state.user = member
      const tutorialStatus = member.tutorialStatus as keyof typeof EnumTutorialStatus
      state.tutorialStatus = tutorialStatus
    },
    setTokens(state: any, tokens: any) {
      state.tokens.accessToken = tokens.accessToken
      state.tokens.refreshToken = tokens.refreshToken
    },
    setPermissions(state: any, permissions: any[]) {
      state.permissions = permissions
    },
    reset(state: any) {
      state.user = {}
      state.tokens = {}
      state.permissions = []
    },
    setTokenInCookie(state: any) {
      const ACCESS_TOKEN_DOMAIN = getSubdomain()
      Cookies.set(ACCESS_TOKEN_KEY, state.tokens.accessToken, {
        expires: 1,
        secure: true,
        path: '/',
        domain: ACCESS_TOKEN_DOMAIN
      })
      Cookies.set(REFRESH_TOKEN_KEY, state.tokens.refreshToken, {
        expires: 1,
        secure: true,
        path: '/',
        domain: ACCESS_TOKEN_DOMAIN
      })
    },
    setTutorialStatus(state: any, status: EnumTutorialStatus) {
      state.tutorialStatus = status
    },
    removeTokenFromCookie() {
      const ACCESS_TOKEN_DOMAIN = getSubdomain()
      Cookies.remove(ACCESS_TOKEN_KEY, { path: '/', domain: ACCESS_TOKEN_DOMAIN })
      Cookies.remove(REFRESH_TOKEN_KEY, { path: '/', domain: ACCESS_TOKEN_DOMAIN })
    },
    resetNotRevealValue(state: any) {
      state.userEmail = ''
      state.userPassword = ''
    },
    setNotRevealValue(
      state: any,
      notRevealValue: { userEmail: string; userPassword: string; statusCode: EnumHttpStatusCode }
    ) {
      if (notRevealValue.userEmail) {
        state.userEmail = notRevealValue.userEmail
      }
      if (notRevealValue.userPassword) {
        state.userPassword = notRevealValue.userPassword
      }
    }
  },
  actions: {
    /**
     * Login
     *
     * @param {{ commit: any, state: any }} { commit, state }
     * @param {MemberLogin} user
     */
    async login({ commit }: { commit: any }, user: MemberLogin) {
      const payload = new LoginParam({ email: user.email, password: user.password })
      const resp = await auth.login(payload)
      const loginResp = new LoginRsp(resp.data)
      commit('setTokens', loginResp)
      commit('setUser', loginResp.memberProfile)
      commit('setTokenInCookie')
    },

    /**
     * Logout
     *
     * @param {{ commit: any, state: any }} { commit, state }
     */
    async logout({ commit }: { commit: any }, payload: LogoutOptions) {
      const logoutOptions: LogoutOptions =
        payload || new LogoutOptions({ isRemoveTokenFromCookie: true })
      try {
        await auth.logout()
        if (logoutOptions.isRemoveTokenFromCookie) {
          commit('removeTokenFromCookie')
        }
      } catch (err) {
        //throw err
      } finally {
        commit('reset')
        commit('routeRecord/clean', null, { root: true })
        if (logoutOptions.isForced) {
          await router.replace({
            name: 'login',
            query: { forceSignOut: EnumForceLogoutType.IdleTimeout }
          })
        } else {
          await router.replace({ name: 'login' })
        }
        window.location.reload()
      }
    },

    /**
     * Refresh Token
     *
     * @param {{ commit: any, state: any }} { commit, state }
     * @returns {Promise<boolean>}
     */
    async refreshToken({ commit, state }: { commit: any; state: any }) {
      if (!state.tokens || !state.tokens.refreshToken) {
        return false
      }
      try {
        const resp = await auth.refreshToken(state.tokens.refreshToken)
        const refreshResp = new LoginRsp(resp.data)
        commit('setTokens', resp.data)
        commit('setUser', refreshResp.memberProfile)
        commit('setTokenInCookie')
        return true
      } catch (err) {
        commit('removeTokenFromCookie')
        commit('reset')
        commit('routeRecord/clean', null, { root: true })
        await router.replace({ name: 'login' })
      }
    },

    /**
     * Refresh user store (Cookie-based)
     *
     * @param {{ commit: any }} { commit }
     */
    async refresh({ commit, dispatch }: { commit: any; dispatch: any }) {
      const accessTokenInCookie = Cookies.get(ACCESS_TOKEN_KEY)
      const refreshTokenInCookie = Cookies.get(REFRESH_TOKEN_KEY)
      if (accessTokenInCookie) {
        commit('setTokens', {
          accessToken: accessTokenInCookie,
          refreshToken: refreshTokenInCookie
        })
        const resp = await member.getMemberProfile()
        const memberProfile = new MemberProfile(resp.data)
        commit('setUser', memberProfile)
        await dispatch('subscription/getMySubscriptionStatus', null, { root: true })
      }
    },

    /**
     * Get access token from cookie
     *
     */
    getAccessTokenFromCookie(): string | null {
      const accessTokenInCookie = Cookies.get(ACCESS_TOKEN_KEY)
      return accessTokenInCookie
    },

    /**
     * Get member profile
     */
    async getMember({ commit }: { commit: any }) {
      const resp = await member.getMemberProfile()
      const memberProfile = new MemberProfile(resp.data)
      commit('setUser', memberProfile)
      return memberProfile
    },

    /**
     * Set this user tutorial status
     */
    async setTutorialStatus({ commit }: { commit: any }, tutorialStatus: EnumTutorialStatus) {
      commit('setTutorialStatus', tutorialStatus)
      await member.setTutorialStatus(tutorialStatus)
    },

    /**
     * Member consent eula
     */
    async consentEula() {
      await member.consentEula()
    },

    /**
     * set verification email to token owner
     */
    async sendVerificationEmail() {
      await member.sendVerificationEmail()
    }
  },
  namespaced: true
}
